import { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandPointer } from "@fortawesome/free-solid-svg-icons";

/**
 * Mobile drag handle component that immediately starts dragging when touched
 * and maintains visual presence during dragging
 */
export function MobileDragHandle({
  widget,
  onDragStart,
  isLightBackground = false,
  isDragging = false,
}) {
  const handleRef = useRef(null);

  useEffect(() => {
    const handleElement = handleRef.current;
    if (!handleElement) return;

    const handleTouchStart = (e) => {
      // Prevent any default touch behaviors immediately
      e.preventDefault();
      e.stopPropagation();

      // Call the drag start handler immediately
      if (onDragStart) {
        onDragStart(e, widget.id);
      }
    };

    // Add touch event listener with non-passive to allow preventDefault
    handleElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });

    return () => {
      handleElement.removeEventListener("touchstart", handleTouchStart);
    };
  }, [widget.id, onDragStart]);

  // Choose handle style based on background and drag state
  const getHandleStyle = () => {
    if (isDragging) {
      // More prominent during dragging
      return isLightBackground
        ? "bg-black/40 border-black/50 ring-2 ring-black/30"
        : "bg-white/40 border-white/50 ring-2 ring-white/30";
    }
    // Normal state
    return isLightBackground
      ? "backdrop-blur-md bg-black/20 border-black/30"
      : "backdrop-blur-md bg-white/20 border-white/30";
  };

  const iconColor = isLightBackground ? "text-black" : "text-white";
  const hoverStyle = !isDragging
    ? isLightBackground
      ? "hover:bg-black/30"
      : "hover:bg-white/30"
    : "";

  return (
    <div
      ref={handleRef}
      className={`absolute -top-7 left-1/2 transform -translate-x-1/2 z-[60] 
                 ${getHandleStyle()} rounded-full w-12 h-12
                 shadow-lg mobile-drag-handle
                 select-none flex items-center justify-center
                 ${hoverStyle} transition-all duration-200
                 ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
      style={{
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        // Ensure handle stays visible and interactive during drag
        pointerEvents: "auto",
      }}
    >
      <FontAwesomeIcon
        icon={faHandPointer}
        className={`${iconColor} text-lg ${isDragging ? "scale-110" : ""} transition-transform duration-150`}
      />
    </div>
  );
}
